import React, { memo, useEffect, useState } from "react";
import { compose } from "redux";
import { connect, useSelector } from "react-redux";
import { reduxForm } from "redux-form";
import { createStructuredSelector } from "reselect";
import {
  clearData,
  createInitialOrder,
  fetchPaymentRequest,
  postPaymentRequest,
  uploadSignature,
  confirmPaymentOption,
  fetchReferralCode,
  fetchUpfrontPaymentDetails,
  processUpfrontPayment,
  fetchPaymentStatus,
  updateOrder,
  updateOMSOrder,
  fetchUserOrders,
  setData
} from "ducks/actions";
import {
  makeSelectIsFetchingData,
  makeSelectOrder,
  makeSelectOrderParams,
  makeSelectPayMonthlyItems,
  makeSelectPayTodayItems,
  makeSelectDeliveryDetailsItems,
  makeSelectReferralCode,
  makeSelectPaymentRequest,
  makeSelectOrderSummaryForm,
  makeSelectOrderSummaryFormInitialValues,
  makeSelectOrderSummaryPayload,
  makeSelectESigned,
  makeSelectPaymentModes,
  makeSelectFormattedSelectedNumber,
  makeSelectAddons,
  makeSelectDefaultPlanAddons,
  makeSelectPlanDetails,
  makeSelectDefaultPromoCodeOffers,
  makeSelectSessionToken,
  makeSelectPayDevice,
  makeSelectWorkflow,
  makeSelectOrderRef,
  makeSelectPersonalDetailsForm,
  makeSelectPaymentDetails,
  makeSelectPlanName,
  makeSelectCompleteOrderParams,
  makeSelectIsPortingUser,
  makeSelectUserDetailsOrderUpdatePayload,
  makeSelectIsPortingUserWithPortingNumber,
  makeSelectConnectionDetailsForm,
  makeSelectChannelReqOrderParams,
  makeSelectShowSectionListModal,
  makeSelectMyInfoIsUsed,
  makeSelectIsWalletPromoCode,
  makeSelectAppSettings,
  makeSelectDeliveryInfoForm,
  makeSelectValidateOrderError
} from "ducks/selectors";
import SmartRender from "containers/SmartRender";
import { paths, forms } from "utils/constants";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";
import get from "lodash/get";
import find from "lodash/find";
import Spinner, { styles } from "components/Spinner";
import usePaymentResponseHandler from "hooks/usePaymentResponseHandler";
import { SIM_TYPES, CONFLICTING_ORDERS_STATUS } from "utils/constants";
import {
  gotoUrl,
  sortAndFilterOrders,
  checkOrderParamsIsValid,
  getChannelFromUrl
} from "utils/helpers";
import { changeFormField } from "ducks/actions/index";

const OrderSummaryHOC = Component =>
  function Comp(props) {
    const {
      channelRequiredOrderParams,
      order,
      orderParams,
      clearOrder,
      createInitialOrder,
      updateOMSOrder,
      clearPaymentRequest,
      clearESignature,
      referralCode,
      isCreatingOrder,
      isPortingUserWithPortingNumber,
      clearMyInfoDomain,
      clearPaymentDetails,
      token,
      personalDetailFormsValues,
      formValues,
      myInfoIsUsed,
      isWalletPromoCode,
      appSettings,
      changeField
    } = props;
    /**
     * Initial order creation is enabled via configurations.
     * Some countries (eg: JP) do not initial order creation until consent is provided
     */

    const workflow = useSelector(makeSelectWorkflow());
    const currentWorkflowPage = find(
      workflow,
      page => page.path === window.location.pathname
    );
    const shouldCreateInitialOrder = get(
      currentWorkflowPage,
      "createInitialOrder",
      true
    );

    const [lastPromoCode, setLastPromoCode] = useState(
      get(referralCode, "value")
    );
    const [promoFlag, setPromoCode] = useState(false);

    const [canRequestPayment, setCanRequestPayment] = useState(false);
    const orderCallback = (_, error) => {
      !error && setCanRequestPayment(true);
    };

    useEffect(
      () => {
        // const orderRef = get(order, "order_ref", null);
        // !isPortingUserWithPortingNumber is for; if user wants to port in number from users account, initial order has been already created in the
        // user-details page. So we shouldn't clear the order here.
        const ekycForWeb =
          getChannelFromUrl() === "web" &&
          get(appSettings, "enable_ekyc_for_web");
        const ekycForCombo =
          getChannelFromUrl() === "combo" &&
          get(appSettings, "enable_ekyc_for_combo");
        const esimChannel = get(order, "channel") === SIM_TYPES.ESIM;
        const ekycEnabled = esimChannel || ekycForWeb || ekycForCombo;
        if (!ekycEnabled && !isPortingUserWithPortingNumber) {
          clearPaymentRequest();
          clearPaymentDetails();
          clearESignature();
          clearMyInfoDomain();
          if (shouldCreateInitialOrder) {
            clearOrder();
            setCanRequestPayment(false);

            if (get(personalDetailFormsValues, "upload_id_later")) {
              const docType = get(personalDetailFormsValues, "doc_type.value");
              const nric = get(orderParams, "user_detail.nric");
              const detailObj = {
                details: [
                  {
                    doc_type: docType,
                    value: nric
                  }
                ]
              };
              orderParams.documents = detailObj;
            }

            createInitialOrder(
              orderParams,
              orderCallback,
              channelRequiredOrderParams
            );
          }
        }
        if (ekycEnabled && token) {
          setCanRequestPayment(false);
          updateOMSOrder(
            { token, ...orderParams },
            orderCallback,
            channelRequiredOrderParams
          );
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [shouldCreateInitialOrder, lastPromoCode]
    );

    useEffect(
      () => {
        if (
          shouldCreateInitialOrder &&
          get(order, "channel") !== SIM_TYPES.ESIM &&
          !isEmpty(order.error) &&
          !isPortingUserWithPortingNumber
        ) {
          clearOrder();
          clearMyInfoDomain();
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [order.error, shouldCreateInitialOrder]
    );

    useEffect(
      () => {
        const promoCode = get(referralCode, "value");
        promoCode && setPromoCode(true);
        if (promoCode !== lastPromoCode) {
          setLastPromoCode(promoCode);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [referralCode, lastPromoCode, setLastPromoCode]
    );

    useEffect(
      () => {
        const noUpfrontPayment = get(props, "payTodayItems.totalAmount") < 1;
        const showWallet =
          noUpfrontPayment && myInfoIsUsed && isWalletPromoCode;
        const defaultWalletPayment =
          showWallet && get(appSettings, "enable_wallet_payment_default");
        const defaultPaymentType = defaultWalletPayment
          ? "wallet"
          : "recurring";
        if (formValues?.payment_type !== defaultPaymentType) {
          changeField("payment_type", defaultPaymentType);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    return isCreatingOrder && !promoFlag ? (
      <Spinner size={200} sx={styles.fullPage} />
    ) : (
      <Component {...props} canRequestPayment={canRequestPayment} />
    );
  };

const mapStateToProps = createStructuredSelector({
  personalDetailFormsValues: makeSelectPersonalDetailsForm(),
  formValues: makeSelectOrderSummaryForm(),
  payload: makeSelectOrderSummaryPayload(),
  order: makeSelectOrder(),
  paymentRequest: makeSelectPaymentRequest(),
  orderParams: makeSelectOrderParams(),
  isCreatingOrder: makeSelectIsFetchingData("order"),
  isFetchingPaymentRequest: makeSelectIsFetchingData("paymentRequest"),
  payMonthlyItems: makeSelectPayMonthlyItems(),
  payTodayItems: makeSelectPayTodayItems("OrderSummary"),
  deliveryDetailsItems: makeSelectDeliveryDetailsItems(),
  referralCode: makeSelectReferralCode(),
  eSigned: makeSelectESigned(),
  paymentModes: makeSelectPaymentModes(),
  initialValues: makeSelectOrderSummaryFormInitialValues(),
  selectedNumber: makeSelectFormattedSelectedNumber(),
  addons: makeSelectAddons(),
  defaultPlanAddons: makeSelectDefaultPlanAddons(),
  planDetails: makeSelectPlanDetails(),
  token: makeSelectSessionToken(),
  defaultPromoCodeOffers: makeSelectDefaultPromoCodeOffers(),
  selectedPhone: makeSelectPayDevice(),
  orderRef: makeSelectOrderRef(),
  planName: makeSelectPlanName(),
  isFetchingPaymentDetails: makeSelectIsFetchingData("paymentDetails"),
  paymentDetails: makeSelectPaymentDetails(),
  completeOrderParams: makeSelectCompleteOrderParams(),
  isPortingUser: makeSelectIsPortingUser(),
  orderUpdatePayload: makeSelectUserDetailsOrderUpdatePayload(),
  isPortingUserWithPortingNumber: makeSelectIsPortingUserWithPortingNumber(),
  connectionDetails: makeSelectConnectionDetailsForm(),
  channelRequiredOrderParams: makeSelectChannelReqOrderParams(),
  showSectionListModal: makeSelectShowSectionListModal(),
  myInfoIsUsed: makeSelectMyInfoIsUsed(),
  isWalletPromoCode: makeSelectIsWalletPromoCode(),
  appSettings: makeSelectAppSettings(),
  deliveryInfoFormValues: makeSelectDeliveryInfoForm(),
  validateOrderError: makeSelectValidateOrderError()
});

export function mapDispatchToProps(dispatch) {
  return {
    clearOrder: () => {
      dispatch(clearData("order"));
    },
    clearPaymentRequest: () => {
      dispatch(clearData("paymentRequest"));
    },
    clearESignature: () => {
      dispatch(clearData("eSignature"));
    },
    clearMyInfoDomain: () => {
      dispatch(clearData("myInfoDetails"));
    },
    createInitialOrder: (orderParams, callback, channelRequiredOrderParams) => {
      if (checkOrderParamsIsValid(orderParams, channelRequiredOrderParams)) {
        dispatch(createInitialOrder(orderParams, callback));
      }
    },
    updateOMSOrder: (orderParams, callback, channelRequiredOrderParams) => {
      if (checkOrderParamsIsValid(orderParams, channelRequiredOrderParams)) {
        dispatch(updateOMSOrder(orderParams, callback));
      }
    },
    fetchPaymentRequest: params => {
      dispatch(fetchPaymentRequest(params));
    },
    postPaymentRequest: (params, callback) => {
      dispatch(postPaymentRequest(params, callback));
    },
    confirmPaymentOption: (orderRef, cb) => {
      dispatch(confirmPaymentOption({ order_ref: orderRef }, cb));
    },
    validateReferralCode: (referral_code, plan_name, device_sku = null) => {
      const data = {
        referral_code,
        plan_name,
        ...(device_sku && { device_sku })
      };
      dispatch(fetchReferralCode(data));
    },
    resetReferralCode: () =>
      dispatch(clearData(["referralCode", "selectedAddons"])),
    uploadSignature: payload => dispatch(uploadSignature(payload)),
    onSubmit: (_, payload) => {
      dispatch(postPaymentRequest(payload, usePaymentResponseHandler));
    },
    fetchUpfrontPaymentDetails: (params, callback) => {
      dispatch(fetchUpfrontPaymentDetails(params, callback));
    },
    processUpfrontPayment: (params, callback) => {
      dispatch(processUpfrontPayment(params, callback));
    },
    fetchPaymentStatus: (params, callback) => {
      dispatch(fetchPaymentStatus(params, callback));
    },
    updateOrder: (payload, callback) =>
      dispatch(updateOrder(payload, callback)),
    clearPaymentDetails: () => {
      dispatch(clearData("paymentDetails"));
    },
    fetchUserOrdersAndNavigateToManageOrder: () => {
      dispatch(
        fetchUserOrders({}, orders => {
          const filteredOrders = sortAndFilterOrders(orders);
          if (!isEmpty(filteredOrders)) {
            gotoUrl(paths.MANAGE_ORDER, true, {
              reset: null,
              iccid: null
            });
          }
        })
      );
    },
    setSectionListModal: (value = false) => {
      dispatch(
        setData({
          key: "showSectionListModal",
          data: { enabled: value }
        })
      );
    },
    changeField: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.ORDER_SUMMARY_FORM,
          field: field,
          value: value
        })
      );
    }
  };
}

export const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  createInitialOrderAndNavigate: navigate => {
    const { orderParams } = stateProps;

    dispatchProps.createInitialOrder(orderParams, (_, error) => {
      const { status } = error || {};
      if (isEmpty(error)) {
        isFunction(navigate) && navigate();
      } else if (status === CONFLICTING_ORDERS_STATUS) {
        dispatchProps.fetchUserOrdersAndNavigateToManageOrder();
      }
    });
  },
  createInitialOrderAndSubmit: (navigate, payload) => {
    dispatchProps.createInitialOrder(stateProps.orderParams, (_, error) => {
      if (isEmpty(error)) {
        dispatchProps.onSubmit(navigate, payload);
      }
    });
  },
  // Update order when user in the account porting flow
  updateOrderAndNavigate: navigate => {
    dispatchProps.updateOrder(stateProps.orderUpdatePayload, (_, error) => {
      if (isEmpty(error)) {
        navigate();
      }
    });
  }
});

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.ORDER_SUMMARY_FORM,
    destroyOnUnmount: false
  }),
  memo
)(OrderSummaryHOC(SmartRender));
